<template>
  <div class="flex flex-col h-full md:max-h-[80vh] md:min-h-[60vh]">
    <!-- Header -->
    <div
      class="flex items-center justify-between pb-2 border-b border-border -mt-2 -mx-2 px-2 shrink-0"
    >
      <div class="flex items-center gap-2">
        <h2 class="text-base font-semibold text-text-primary m-0">Your Order</h2>
        <span
          v-if="orderItems.length > 0"
          class="flex items-center justify-center min-w-[1.5rem] h-6 w-6 rounded-full border border-border text-text-primary text-sm font-semibold"
        >
          {{ orderItems.length }}
        </span>
      </div>
      <button
        type="button"
        class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-text-primary hover:bg-gray-200 transition-colors"
        aria-label="Close"
        @click="emit('close')"
      >
        <Icon name="x" :size="20" weight="bold" />
      </button>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto px-5 py-3">
      <!-- Loading skeleton -->
      <div v-if="loading" class="flex flex-col gap-3">
        <div
          v-for="i in 3"
          :key="i"
          class="flex items-center justify-between py-4 border-b border-border animate-pulse"
        >
          <div class="flex flex-col gap-2 flex-1">
            <div class="h-4 bg-gray-200 rounded w-2/3" />
            <div class="h-3 bg-gray-200 rounded w-1/4" />
          </div>
          <div class="flex flex-col items-end gap-2">
            <div class="h-4 bg-gray-200 rounded w-16" />
            <div class="h-5 bg-gray-200 rounded-full w-20" />
          </div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="flex flex-col items-center justify-center py-10 gap-3">
        <Icon name="warning-circle" :size="40" weight="regular" color="#9ca3af" />
        <p class="text-sm text-text-secondary text-center">{{ error }}</p>
        <button type="button" class="text-primary text-sm underline" @click="fetchOrderCart">
          Try again
        </button>
      </div>

      <!-- Empty -->
      <div
        v-else-if="orderItems.length === 0"
        class="flex flex-col items-center justify-center py-10 gap-3"
      >
        <Icon name="receipt" :size="48" weight="regular" color="#9ca3af" />
        <p class="text-sm text-text-secondary">No orders yet for this session.</p>
      </div>

      <!-- Order item list -->
      <div v-else class="space-y-4">
        <!-- Billing Items -->
        <div v-if="billingItems.length > 0">
          <div
            v-for="(item, index) in billingItems"
            :key="item.id ?? `billing-${index}`"
            class="flex items-start justify-between py-4 border-b border-border last:border-b-0"
          >
            <!-- Left: name + variant/count -->
            <div class="flex-1 min-w-0 pr-3">
              <p class="text-sm font-semibold text-text-primary m-0 leading-snug">
                {{ item.item.title }}
              </p>
              <p class="text-xs text-text-secondary m-0 mt-0.5">
                <span v-if="item.item_count > 0">&times; {{ item.item_count }}</span>
              </p>
            </div>

            <!-- Right: price + status -->
            <div class="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span class="text-sm font-semibold text-text-primary">
                {{ formatRs(item.item_total) }}
              </span>
              <span
                v-if="item.status_label_txt"
                class="text-xs font-medium px-2.5 py-0.5 rounded-full"
                :class="statusClass(item.status_label_txt)"
              >
                {{ item.status_label_txt }}
              </span>
            </div>
          </div>
        </div>

        <!-- Non Billing Items Section -->
        <div v-if="nonBillingItems.length > 0" class="pt-3 border-t border-border">
          <div class="flex items-center justify-between mb-1">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-primary/70 m-0">
              Non Billing Items
            </h3>
          </div>

          <div>
            <div
              v-for="(item, index) in nonBillingItems"
              :key="item.id ?? `non-billing-${index}`"
              class="flex items-start justify-between py-3.5 border-b border-border last:border-b-0"
            >
              <!-- Left: name + count -->
              <div class="flex-1 min-w-0 pr-3">
                <p class="text-sm font-medium text-text-primary m-0 leading-snug">
                  {{ item.item.title }}
                </p>
                <p class="text-xs text-text-secondary m-0 mt-0.5">
                  <span v-if="item.item_count > 0">&times; {{ item.item_count }}</span>
                </p>
              </div>

              <!-- Right: status without price -->
              <div class="flex flex-col items-end gap-1.5 flex-shrink-0">
                <span
                  v-if="item.status_label_txt"
                  class="text-xs font-medium px-2.5 py-0.5 rounded-full"
                  :class="statusClass(item.status_label_txt)"
                >
                  {{ item.status_label_txt }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      v-if="!loading && !error && orderItems.length > 0"
      class="flex-shrink-0 px-5 pt-3 pb-6 bg-white"
    >
      <div class="flex items-center justify-between mb-4">
        <span class="text-sm text-text-secondary font-medium">Total Price</span>
        <span class="text-lg font-bold text-primary">{{ formatRs(cartTotal) }}</span>
      </div>
      <!-- End Dining is intentionally hidden for now. Re-enable the action here when needed again. -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import Icon from '@/components/atoms/Icon.vue'
import { orderApi } from '@/api/order'
import { useAuthStore } from '@/stores/auth'
import { useOrderStore } from '@/stores/order'
import { useActiveReservation } from '@/composables/features/useActiveReservation'
import { useCurrency } from '@/composables/core/useCurrency'
import { useNotifications } from '@/composables/core/useNotifications'
import { useReservationSessionGuard } from '@/composables/features/useReservationSessionGuard'
import { parseApiError } from '@/utils/errorHandler'

// ── Raw API response types ─────────────────────────────────────────────────────

interface ApiCategory {
  id?: number | string
  title?: string
  billing?: number | string | boolean
}

interface ApiMenuItemCategory {
  id?: number | string
  category_id?: number | string
  item_id?: number | string
  category?: ApiCategory
}

interface ApiOrderItem {
  id: number
  item_id: number
  order_id: number
  item_count: number
  item_comment: string | null
  attribute_id: number
  item_total: number
  amount: number
  discount: number
  item_status: number
  item_total_amount: number
  status_label_txt: string
  status_label_sm: string
  item: {
    id: number
    title: string
    price: number
    menu_category?: ApiMenuItemCategory[]
    menu_category_all?: ApiMenuItemCategory[]
    item_attributes_filter?: Array<{
      attribute_values?: Array<{
        id: number
        menu_item_price: number
        attribute_value_id: number
      }>
    }>
  }
}

interface ApiOrderResponse {
  id: number
  order_total: number
  order_items: ApiOrderItem[]
  order_items_ready_to_serve?: ApiOrderItem[]
  table_no?: string
  reservation_id?: number
}

// ── Props / emits ──────────────────────────────────────────────────────────────

interface Props {
  open: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  'order-closed': []
}>()

// ── State ──────────────────────────────────────────────────────────────────────

const route = useRoute()
const authStore = useAuthStore()
const orderStore = useOrderStore()
const { getStoredActiveReservation } = useActiveReservation()
const { showError } = useNotifications()
const { validateReservationStillActive } = useReservationSessionGuard()

const loading = ref<boolean>(false)
const error = ref<string>('')
const orderData = ref<ApiOrderResponse | null>(null)

// ── Derived data ───────────────────────────────────────────────────────────────

const orderItems = computed((): ApiOrderItem[] => orderData.value?.order_items ?? [])

const billingItems = computed((): ApiOrderItem[] =>
  orderItems.value.filter(item => !isNonBillingItem(item))
)

const nonBillingItems = computed((): ApiOrderItem[] =>
  orderItems.value.filter(item => isNonBillingItem(item))
)

const cartTotal = computed((): number => orderData.value?.order_total ?? 0)

const activeReservationId = computed((): number | null => {
  const stored = getStoredActiveReservation()
  const fromStored = stored?.reservationId
  const fromAuth = authStore.activeReservationId
  const fromUrl = route.query.reservation_id as string | undefined

  const id = fromStored ?? fromAuth ?? fromUrl
  if (!id) return null
  const n = Number(id)
  return Number.isNaN(n) ? null : n
})

// ── Helpers ────────────────────────────────────────────────────────────────────

const { formatDefaultPrice: formatRs } = useCurrency()

function isNonBillingItem(orderItem: ApiOrderItem): boolean {

  const item = orderItem.item;
  if (!item) return false

  // 1. menu_category on item
  if (Array.isArray(item.menu_category) && item.menu_category.length > 0) {
    for (const mc of item.menu_category) {
      const b = mc.category?.billing
      if (b !== undefined && (b === 0 || b === '0' || b === false)) {
        return true
      }
    }
  }

  return false
}

// function getAttributeName(item: ApiOrderItem): string {
//   if (!item.attribute_id) return ''
//   const attrs = item.item?.item_attributes_filter ?? []
//   for (const attr of attrs) {
//     const match = (attr.attribute_values ?? []).find(v => v.id === item.attribute_id)
//     if (match) {
//       // Return the price as a fallback label when we only have the price
//       return formatRs(match.menu_item_price)
//     }
//   }
//   return ''
// }

function statusClass(status: string): string {
  const s = status.toLowerCase()
  if (s.includes('pending')) return 'bg-[#FFF4D6] text-[#D68600]'
  if (s.includes('prepar')) return 'bg-[#F2E6FF] text-[#9B51E0]'
  if (s.includes('served')) return 'bg-green-light text-green'
  if (s.includes('serve') || s === 'to-serve') return 'bg-[#E5F0FF] text-[#2F6FED]'
  if (s.includes('done') || s.includes('complet')) return 'bg-green-light text-green'
  if (s.includes('cancel')) return 'bg-[#FDE8E8] text-[#D14343]'
  return 'bg-[#F3F4F6] text-[#6B7280]'
}

// ── API calls ──────────────────────────────────────────────────────────────────

async function fetchOrderCart(): Promise<void> {
  const resId = activeReservationId.value
  if (!resId) {
    error.value = 'No active reservation found.'
    return
  }
  loading.value = true
  error.value = ''
  try {
    const res = await orderApi.getMyOrderCart({ active_reservation_id: String(resId) })
    if (res?.error) throw new Error(parseApiError(res).message)

    // Response is the order object directly (or wrapped in .data)
    const payload = (res as { data?: unknown }).data ?? res
    orderData.value = payload as ApiOrderResponse
    // Sync active order item count to the store so the bottom nav badge updates
    const items = (orderData.value as ApiOrderResponse)?.order_items ?? []
    orderStore.setActiveOrderItemCount(items.length)
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'Failed to load your orders.'
  } finally {
    loading.value = false
  }
}

async function validateActiveReservationForDrawer(): Promise<boolean> {
  const resId = activeReservationId.value
  if (!resId) {
    error.value = 'No active reservation found.'
    orderData.value = null
    orderStore.setActiveOrderItemCount(0)
    return false
  }

  const validation = await validateReservationStillActive(resId)
  if (!validation.ok) {
    error.value = validation.message
    orderData.value = null
    orderStore.setActiveOrderItemCount(0)
    showError(validation.message)
    emit('close')
    return false
  }

  return true
}

// ── Lifecycle ──────────────────────────────────────────────────────────────────

watch(
  () => props.open,
  open => {
    if (open) {
      void (async () => {
        const isActive = await validateActiveReservationForDrawer()
        if (!isActive) return
        await fetchOrderCart()
      })()
      return
    }

    error.value = ''
    orderData.value = null
  },
  { immediate: true }
)
</script>
